import type { CSSProperties } from "react";

function ShimmerBlock(props: { className?: string; style?: CSSProperties; testId?: string }) {
  return (
    <div
      className={`relative overflow-hidden rounded-md bg-muted ${props.className ?? ""}`}
      style={props.style}
      data-testid={props.testId}
    >
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/30 to-transparent dark:via-white/8" />
    </div>
  );
}

const TAB_WIDTHS = [88, 56, 56, 72, 108];

export function ReportPageSkeleton() {
  return (
    <div
      className="relative flex flex-col bg-[hsl(var(--report-bg))] h-[calc(100dvh-var(--feedback-manual-lane))] overflow-hidden"
      data-testid="report-page-skeleton"
    >
      {/* Header — same 1400px cap and padding as ReportHeader */}
      <div className="mx-auto w-full max-w-[1400px] shrink-0 px-4 md:px-8 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <ShimmerBlock className="h-3 w-10 rounded-full" />
              <ShimmerBlock className="h-3 w-28 rounded-full" />
            </div>
            <ShimmerBlock className="h-4 w-64 rounded" />
          </div>
          <ShimmerBlock className="h-9 w-32 rounded-md" />
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1400px] flex-1 min-h-0 flex flex-col px-4 md:px-8 overflow-visible">
        {/* Hero: headline card (flex-[2]) + cover (w-1/3), same split as ReportLandingPage */}
        <div className="flex gap-4 min-h-[220px]">
          <div className="flex-[2] rounded-2xl bg-[hsl(var(--report-card))] flex flex-col justify-between gap-4 px-5 py-6 md:px-8 md:py-8 min-w-0">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                <ShimmerBlock className="h-3 w-44 rounded-full" />
                <ShimmerBlock className="h-3 w-28 rounded-full" />
                <ShimmerBlock className="h-3 w-20 rounded-full" />
              </div>
              <ShimmerBlock className="h-8 w-full rounded-lg" />
              <ShimmerBlock className="h-8 w-[80%] rounded-lg" />
              <div className="flex flex-col gap-2 mt-1">
                <ShimmerBlock className="h-3.5 w-full rounded" />
                <ShimmerBlock className="h-3.5 w-[85%] rounded" />
              </div>
            </div>
            {/* Summary, Deck, Chat, Analyst, Survey data */}
            <div className="flex items-center gap-2 flex-wrap">
              {TAB_WIDTHS.map((width, index) => (
                <ShimmerBlock
                  key={index}
                  className="h-9 rounded-full"
                  style={{ width }}
                  testId={`report-skeleton-tab-${index}`}
                />
              ))}
            </div>
          </div>

          <ShimmerBlock
            className="hidden md:block w-1/3 shrink-0 self-stretch rounded-2xl"
            testId="report-skeleton-cover"
          />
        </div>

        {/* Summary slots: Featured takes the headline width, Quotes is a third */}
        <div
          className="py-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_calc(100%/3)] gap-4 items-start"
          data-testid="report-skeleton-slots"
        >
          <div
            className="rounded-2xl border border-border/60 px-5 py-5 flex flex-col gap-4 min-w-0"
            data-testid="report-skeleton-featured"
          >
            <ShimmerBlock className="h-4 w-16 rounded" />
            <ShimmerBlock className="h-4 w-3/4 rounded" />
            {/* The plot itself is whatever the study featured. One block, not a chart type. */}
            <ShimmerBlock className="h-48 w-full rounded-xl" testId="report-skeleton-featured-plot" />
          </div>

          <div
            className="rounded-2xl border border-border/60 px-5 py-5 flex flex-col gap-4 min-w-0"
            data-testid="report-skeleton-quotes"
          >
            <ShimmerBlock className="h-4 w-14 rounded" />
            <ShimmerBlock className="h-4 w-full rounded" />
            <ShimmerBlock className="h-3 w-2/3 rounded" />
            <div className="flex flex-col gap-2">
              <ShimmerBlock className="h-5 w-full rounded" />
              <ShimmerBlock className="h-5 w-[92%] rounded" />
              <ShimmerBlock className="h-5 w-[84%] rounded" />
              <ShimmerBlock className="h-5 w-[60%] rounded" />
            </div>
            <div className="flex items-center gap-3">
              <ShimmerBlock className="h-12 w-12 rounded-full shrink-0" />
              <ShimmerBlock className="h-4 w-28 rounded" />
              <ShimmerBlock className="ml-auto h-8 w-8 rounded-full shrink-0" />
            </div>
            <div className="mt-auto flex items-center justify-between">
              <ShimmerBlock className="h-8 w-8 rounded-full" />
              <div className="flex items-center gap-1.5">
                {[0, 1, 2, 3, 4].map((index) => (
                  <ShimmerBlock
                    key={index}
                    className={index === 0 ? "h-1.5 w-5 rounded-full" : "h-1.5 w-1.5 rounded-full"}
                  />
                ))}
              </div>
              <ShimmerBlock className="h-8 w-8 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
